import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { useFeatureGateStatus, type GateStatus } from '@/analytics';
import { DARK_MODE_GATE } from '@/config/feature-gates';
import { getUserSettings, type UserSettings } from '@/lib/api-client';
import { loadStoredTheme, setThemeGate, setThemeSignedIn } from '@/preferences/theme-preference';

interface ThemeSyncProps {
    /** null while Clerk is still loading. */
    signedIn: boolean | null;
    /** The signed-in user's id; null while loading or signed out. */
    userId: string | null;
    gate: GateStatus;
    getToken: () => Promise<string | null>;
    fetchSettings: (token: string) => Promise<UserSettings>;
}

/**
 * Feeds auth + gate state into the theme module and loads the stored theme
 * once per signed-in user. Split from ThemeController so tests drive it with
 * plain props (bun's mock.module is process-global).
 */
export function ThemeSync({ signedIn, userId, gate, getToken, fetchSettings }: ThemeSyncProps) {
    // Once the gate has settled, hold that value if the gate source ever
    // reports 'pending' again (the Statsig browser SDK did on each re-identify,
    // e.g. picking a Language in Appearance). Forwarding that would drop a live
    // theme preview back to the cached theme mid-edit; the next settled value
    // ('on' or 'off') is forwarded as usual.
    const [settledGate, setSettledGate] = useState<GateStatus>(gate);
    if (gate !== 'pending' && gate !== settledGate) setSettledGate(gate);

    // The user whose stored theme has been requested. Keyed on the id, not on
    // the effect's deps: a fresh getToken identity, or the gate re-settling,
    // is not a new user and must not refetch.
    const fetchedFor = useRef<string | null>(null);

    useEffect(() => {
        setThemeGate(settledGate);
    }, [settledGate]);
    useEffect(() => {
        if (signedIn == null) return;
        // A new sign-in (even as the same user) loads the preference again.
        if (!signedIn) fetchedFor.current = null;
        setThemeSignedIn(signedIn);
    }, [signedIn]);
    useEffect(() => {
        // Only fetch when the result can matter: no API call per page load
        // for users outside the dark_mode rollout.
        if (signedIn !== true || settledGate !== 'on' || userId == null) return;
        if (fetchedFor.current === userId) return;
        fetchedFor.current = userId;
        void (async () => {
            try {
                const token = await getToken();
                if (token == null) return;
                const settings = await fetchSettings(token);
                // Dropped if the user signed out or switched meanwhile.
                if (fetchedFor.current === userId) loadStoredTheme(settings.theme);
            } catch {
                // Keep the cached theme; the next sign-in (or page load)
                // retries. So does a null token (return above).
            }
        })();
    }, [signedIn, settledGate, userId, getToken, fetchSettings]);
    return null;
}

/** Mounted once in routes/__root.tsx, and not on chromeless routes: the
 *  diagram embed renders inside the composer's paper page and stays light. */
export function ThemeController() {
    const { isLoaded, isSignedIn, userId, getToken } = useAuth();
    const gate = useFeatureGateStatus(DARK_MODE_GATE);
    return (
        <ThemeSync
            signedIn={isLoaded ? isSignedIn === true : null}
            userId={isLoaded && isSignedIn === true ? (userId ?? null) : null}
            gate={gate}
            getToken={getToken}
            fetchSettings={getUserSettings}
        />
    );
}
